import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil, startCase } from 'lodash'
import classNames from 'classnames'
import { IconSparkles, IconEye, IconEyeOff } from '@tabler/icons-react'

import { Table } from '@/components/tables/Table/Table'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { fetchCallSummaryByCallId } from '@/reducers/callSearch/callSearch.actions'
import { formatContent, getSummaryValue } from '@/utils/helpers'
import { CHANNEL_TYPE } from '@/utils/constants'

import { CopyButton } from '@/components/buttons/CopyButton'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { PoweredByAI } from '@/components/ai/PoweredByAI'
import { renderExtractedDataRows } from './helpers'

export const Summary = ({ callId, callType = CHANNEL_TYPE.CALL }) => {
  const [showEditableSummary, setShowEditableSummary] = useState(true)
  const dispatch = useDispatch()
  const {
    callExplorer: { callSummary, extracted_data },
    loading: { qaScores: callSummaryLoading },
  } = useSelector((state) => state.callSearch)

  const labelColors = {
    reason: 'var(--color-brand)',
    objections: 'var(--color-critical)',
    'next steps': 'var(--color-success)',
    outcome: 'var(--color-magical)',
  }

  const doCallSummariesExist = (callSummary) => {
    if (isEmpty(callSummary.parsed_summary)) {
      return false
    }

    return Object.keys(callSummary.parsed_summary).some((key) => callSummary.parsed_summary[key])
  }

  const getFormattedContent = (key) => {
    const { parsed_summary_v2, parsed_summary, parsed_editable_summary } = callSummary
    if (callSummaryLoading || isEmpty(parsed_summary)) {
      return [false, '']
    }

    let isKeyEdited = false
    let content = isEmpty(parsed_summary_v2) ? parsed_summary[key] : parsed_summary_v2[key]

    if (showEditableSummary && !isEmpty(parsed_editable_summary)) {
      isKeyEdited = content !== parsed_editable_summary[key]
      content = parsed_editable_summary[key]
    }
    return [isKeyEdited, content]
  }

  const summaryContent = getFormattedContent('summary')

  // Per-field timing indicator: an open eye for realtime fields (shown to the
  // agent during the call), a closed eye for the rest (processed after it
  // ended). shown_to_agent is keyed by the same field names as parsed_summary
  // and is absent on older summaries.
  const renderShownToAgentIcon = (key) => {
    const shownToAgent = callSummary.shown_to_agent
    if (isNil(shownToAgent) || isNil(shownToAgent[key])) {
      return null
    }

    const wasShown = shownToAgent[key]
    const EyeIcon = wasShown ? IconEye : IconEyeOff
    const label = wasShown ? 'Shown to agent at end of call' : 'Hidden from agent'

    return (
      <InteractiveTooltip content={label} placement="top">
        <EyeIcon
          className={classNames('shown-to-agent-icon', { 'is-shown': wasShown })}
          size={18}
          aria-label={label}
          data-testid={`shown-to-agent-${key}`}
        />
      </InteractiveTooltip>
    )
  }

  useEffect(() => {
    dispatch(fetchCallSummaryByCallId(callId))
  }, [])

  return (
    <div className="call-metadata-container">
      <div>
        <h2 className="flex-align-center small-gap">
          {`Summary${summaryContent[0] ? ' (Edited)' : ''}`}
          <Pill small magical icon={<IconSparkles />}>
            AI
          </Pill>
          {renderShownToAgentIcon('summary')}
        </h2>
        <PoweredByAI />
        {!isEmpty(callSummary.parsed_editable_summary) && (
          <SegmentedControl
            className="mb"
            fullWidth
            items={[
              {
                label: 'Edited Notes',
                active: showEditableSummary,
                accessor: 'edited',
                onClick: () => setShowEditableSummary(true),
              },
              {
                label: 'Original',
                active: !showEditableSummary,
                accessor: 'original',
                onClick: () => setShowEditableSummary(false),
              },
            ]}
          />
        )}
        {doCallSummariesExist(callSummary) && (
          <div>
            {formatContent(summaryContent[1])}{' '}
            <CopyButton
              content={getSummaryValue(summaryContent[1])}
              tooltipContent="Copy Summary"
              showTooltip
              iconOnly
            />
          </div>
        )}
        {callSummaryLoading && (
          <div className="empty-table">
            <Loader />
          </div>
        )}
        {!callSummaryLoading && doCallSummariesExist(callSummary) && (
          <div className="call-metadata-wrapper">
            <div className="call-metadata-summary-container expanded">
              {Object.keys(
                showEditableSummary && !isEmpty(callSummary.parsed_editable_summary)
                  ? callSummary.parsed_editable_summary
                  : callSummary.parsed_summary
              )
                .filter((key) => key !== 'summary')
                .map((key) => {
                  const [isKeyEdited, content] = getFormattedContent(key)
                  const copiableValue = getSummaryValue(content)

                  return (
                    <div className="summary-item" key={key}>
                      <h3>
                        <div
                          className="summary-color"
                          style={{ background: labelColors[key] || 'var(--color-neutral)' }}
                        />
                        <span>{`${startCase(key)}${isKeyEdited ? ' (Edited)' : ''}`}</span>
                        {renderShownToAgentIcon(key)}
                        <CopyButton
                          content={copiableValue}
                          tooltipContent={`Copy ${startCase(key)}`}
                          showTooltip
                          iconOnly
                        />
                      </h3>
                      <div>{formatContent(content)}</div>
                    </div>
                  )
                })}
            </div>
          </div>
        )}
        {!callSummaryLoading && !doCallSummariesExist(callSummary) && (
          <div>
            <p>
              No {callType} summary associated with this {callType}
            </p>
          </div>
        )}
      </div>
      {!callSummaryLoading && !isEmpty(extracted_data) && (
        <div data-testid="extracted-data-container">
          <h2 className="flex-align-center small-gap">
            Application Details
            <Pill small magical icon={<IconSparkles />}>
              AI
            </Pill>
          </h2>
          <Table compact celled>
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>

            <tbody>{renderExtractedDataRows(extracted_data)}</tbody>
          </Table>
        </div>
      )}
    </div>
  )
}
